import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Anchor } from 'lucide-react';

export const NotFoundPage = () => {
  const { language } = useLanguage();

  const content = {
    it: {
      title: '404',
      subtitle: 'Pagina non trovata',
      description: 'La pagina che stai cercando non esiste o è stata spostata.',
      home: 'Torna alla Home',
      back: 'Torna indietro'
    },
    de: {
      title: '404',
      subtitle: 'Seite nicht gefunden',
      description: 'Die Seite, die Sie suchen, existiert nicht oder wurde verschoben.',
      home: 'Zur Startseite',
      back: 'Zurück'
    },
    fr: {
      title: '404',
      subtitle: 'Page non trouvée',
      description: 'La page que vous recherchez n\'existe pas ou a été déplacée.',
      home: 'Retour à l\'accueil',
      back: 'Retour'
    },
    en: {
      title: '404',
      subtitle: 'Page not found',
      description: 'The page you are looking for does not exist or has been moved.',
      home: 'Back to Home',
      back: 'Go back'
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-[#0B1221] flex items-center justify-center px-6">
      <div className="text-center max-w-xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Animated Anchor Icon */}
          <motion.div
            animate={{ 
              y: [0, -10, 0],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="mb-8"
          >
            <Anchor className="w-24 h-24 mx-auto text-white/20" strokeWidth={1} />
          </motion.div>

          {/* 404 Text */}
          <h1 className="text-8xl sm:text-9xl font-bold text-white/10 mb-4">
            {t.title}
          </h1>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            {t.subtitle}
          </h2>
          
          <p className="text-white/60 text-lg mb-10">
            {t.description}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-white text-[#0B1221] hover:bg-white/90 rounded-full px-8 py-6 text-base"
            >
              <Link to="/">
                <Home className="w-5 h-5 mr-2" />
                {t.home}
              </Link>
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t.back}
            </Button>
          </div>
        </motion.div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
          <svg 
            viewBox="0 0 1440 120" 
            className="w-full text-white/5"
            preserveAspectRatio="none"
          >
            <path 
              fill="currentColor" 
              d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,69.3C960,85,1056,107,1152,101.3C1248,96,1344,64,1392,48L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
